import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { MainLayout } from '../../layouts/main-layout';
import { ModelosSeleccionComponent } from '../../components/shop/modelos-seleccion/modelos-seleccion.component';
import type { Category } from '../../types/category.types';
import { smartTitle } from '../../utils/pipes/smart-capitalize';
import categorias from '../../data-preloaded/grupos-data';
import { MarginWrapper } from '../../layouts/margin-wrapper';
import { VehicleService } from '../../services/vehicle.service';
import { getModelsByBrand } from '../../utils/vehicles/get-vehicles';
import { ModeloVehiculo } from '../../types/vehicle.types';
import { fromSlug, toSlug } from '../../utils/slugs/slug.utils';

@Component({
  selector: 'allas-categorias',
  imports: [
    MainLayout,
    CommonModule,
    RouterModule,
    ModelosSeleccionComponent,
    MarginWrapper,
  ],
  template: `
    <!-- Titulo Principal oculto pero indexable -->
    <h1 class="absolute left-[-9999px]">
      {{ h1Title() }}
    </h1>
    <allas-main-layout>
      <allas-margin-wrapper styleClass="fs-condensed my-8">
        <!-- Breadcrumb -->
        <div
          class="flex flex-wrap items-center text-sm text-gray-600 mb-6 gap-y-2"
        >
          <a routerLink="/" class="hover:text-blue-600">Inicio</a>
          <svg
            class="w-4 h-4 mx-2"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
          <span class="text-gray-900 font-medium">Repuestos</span>
        </div>

        <!-- Categories Grid -->
        <div class="container mx-auto px-4">
          <!-- Popular Categories Section -->
          <div class="mb-12">
            <h2 class="text-2xl font-bold text-gray-900 mb-8">
              REPUESTOS POPULARES
              <!---->
              @if (marca()) {
              <!---->
              PARA
              <!---->
              <span class="text-red-600"> {{ marca().toUpperCase() }}</span>
              <!---->
              } @if (modelo()) {
              <span class="text-red-600"> {{ modelo().toUpperCase() }}</span>
              }
            </h2>
            <div class="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-6">
              @for (category of mainCategories().slice(0, 11); track
              category.id) {
              <a
                [routerLink]="getCategoryLink(category.slug)"
                class="group bg-white rounded-lg border-[1px] border-gray-200 p-4 hover:border-red-300 hover:shadow-lg transition-colors duration-300"
              >
                <div
                  class="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden"
                >
                  <img
                    [src]="category.url_imagen"
                    [alt]="category.name"
                    onerror="this.src='/placeholder.svg'"
                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 bg-white"
                    width="300"
                    height="300"
                    loading="lazy"
                  />
                </div>
                <div
                  class="text-sm font-medium text-gray-800 text-center leading-tight group-hover:text-red-600 transition-colors"
                >
                  {{ category.name }} @if (marca()) { para
                  <span class="text-red-600"> {{ smartTitle(marca()) }}</span>
                  } @if (modelo()) {
                  <span class="text-red-600"> {{ smartTitle(modelo()) }}</span>
                  }
                </div>
              </a>
              }

              <!-- Liquidación Card -->
              <a
                href="/bajo-construccion"
                class="group bg-white rounded-lg border border-gray-200 p-4 hover:border-red-300 hover:shadow-lg transition-all duration-300"
                aria-label="Ver productos en liquidación"
              >
                <div
                  class="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden"
                >
                  <img
                    src="/images/home/clearance.webp"
                    alt="Productos en liquidación"
                    onerror="this.src='/placeholder.svg'"
                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 bg-white"
                  />
                </div>
                <div
                  class="text-sm font-medium text-gray-800 text-center leading-tight group-hover:text-red-600 transition-colors"
                >
                  Liquidación @if (marca()) { para
                  <span class="text-red-600"> {{ smartTitle(marca()) }}</span>
                  } @if (modelo()) {
                  <span class="text-red-600"> {{ smartTitle(modelo()) }}</span>
                  }
                </div>
              </a>
            </div>
          </div>

          <!-- All Categories Section -->
          <div class="mb-12">
            <h2 class="text-2xl font-bold text-gray-900 mb-8">
              COMPRAR POR CATEGORÍA
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              @for (category of allCategories(); track category.id) {
              <a
                [routerLink]="getCategoryLink(category.slug)"
                class="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-red-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                <div class="flex items-center space-x-4">
                  <div
                    class="font-medium text-gray-900 group-hover:text-red-600 transition-colors"
                  >
                    {{ category.name }} @if (marca()) { para
                    <span class="text-red-600"> {{ smartTitle(marca()) }}</span>
                    } @if (modelo()) {
                    <span class="text-red-600">
                      {{ smartTitle(modelo()) }}
                    </span>
                    }
                  </div>
                </div>
                <div
                  class="text-gray-400 group-hover:text-red-500 transition-colors"
                >
                  <svg
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    ></path>
                  </svg>
                </div>
              </a>
              }
            </div>
          </div>

          <!-- Modelos Selection -->
          @if (marca() && !modelo() && modelos().length > 0) {
          <allas-modelos-seleccion [marca]="marca" [modelos]="modelos" />
          }

          <!-- Features Section -->
          <div class="bg-white rounded-lg border border-gray-200 p-8">
            <div class="flex flex-col md:flex-row gap-8 mb-8">
              <h2
                class="text-2xl font-bold text-gray-900 mb-4 w-full md:w-1/4 text-center md:text-right"
              >
                ¿Por qué elegir Allas Repuestos?
              </h2>
              <div
                class="flex flex-col text-gray-600 flex-auto w-full md:w-3/4 [&_p]:mb-6 md:[&_p]:mb-2"
              >
                <p>
                  En Allas Repuestos, nuestra misión es simple: asegurar el
                  rendimiento de su vehículo con calidad y accesibilidad
                  inigualables.
                </p>
                <p>
                  Hemos desarrollado un catálogo exhaustivo que cubre desde el
                  mantenimiento básico, como Filtros de Aire y Aceite, hasta
                  reparaciones complejas, incluyendo Fricciones, Amortiguadores,
                  Tijeras o Brazos de Control, Soportes de Motor y todas las
                  piezas críticas del Sistema de Dirección, Suspensión y Motor.
                </p>
                <p>
                  Para acercar este inventario a su taller o domicilio, contamos
                  con una robusta red de 12 sucursales estratégicamente ubicadas
                  en las principales ciudades de Honduras: San Pedro Sula,
                  Tegucigalpa (Comayagüela, Torocagua, La Era, America,
                  Kennedy), La Ceiba, El Progreso y Choloma.
                </p>
                <p>
                  Esto garantiza que tu repuesto para tu
                  {{ getVehicleText() || 'carro' }} esté siempre al alcance, con
                  el respaldo de las mejores marcas. Escoger a Allas Repuestos
                  significa asociarse con especialistas en la materia. No solo
                  proveemos piezas; ofrecemos soluciones.
                </p>
                <p>
                  Nuestro compromiso con la calidad se refleja en nuestro
                  inventario de marcas líderes y de confianza como Dorman,
                  Nakamoto, GSP North America, Wagner Quickstop y Febest,
                  asegurando que su repuesto cumpla o exceda las
                  especificaciones de fábrica.
                </p>
                <p>
                  Nuestro equipo le brindará la asesoría técnica precisa en
                  productos como Filtro de Aceite de Elemento, Hules de Barra
                  Estabilizadora, Bujes de Suspensión, o Flecha Completa,
                  asegurando que obtenga el repuesto correcto a la primera.
                  Nuestra experiencia se traduce en su tranquilidad y en el
                  rendimiento de su vehículo. Sabemos que el tiempo de
                  inactividad de su vehículo es crítico.
                </p>
                <p>
                  Por ello, hemos optimizado nuestra logística para asegurar una
                  disponibilidad de stock excepcional en todas nuestras
                  ubicaciones y un envío rápido a cualquier punto del país. Ya
                  sea que necesite Embragues, Baterías, Repuestos de Carrocería
                  o Lubricantes, le garantizamos la transparencia total y un
                  excelente respaldo postventa en su compra.
                </p>
                <p>
                  Allas Repuestos es tu aliado estratégico para el mantenimiento
                  de tu {{ getVehicleText() || 'carro' }} y cualquier otro
                  vehículo: una red de 12 tiendas con Calidad Garantizada
                  siempre cerca de ti. Elija la solución inteligente para seguir
                  moviendo el mundo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </allas-margin-wrapper>
    </allas-main-layout>
  `,
})
export class Categorias implements OnInit {
  // Categorías principales para destacar
  mainCategories = signal<Category[]>([...(categorias?.populares || [])]);

  // Todas las categorías disponibles
  allCategories = signal<Category[]>([...(categorias?.grupos || [])]);

  smartTitle = smartTitle;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private title = inject(Title);
  private meta = inject(Meta);
  vehicleService = inject(VehicleService);
  marca = signal<string>('');
  modelo = signal<string>('');
  modelos = signal<ModeloVehiculo[]>([]);
  h1Title = signal<string>('');

  actualRoute = signal<string>('');

  ngOnInit() {
    this.setMetaTags();

    // Detectar marca y modelo desde los parámetros de ruta
    this.actualRoute.set(this.router.url);
    this.route.params.subscribe((params) => {
      this.marca.set(fromSlug(params['marca'] || ''));
      this.modelo.set(fromSlug(params['modelo'] || ''));

      // Cargar modelos si hay marca pero no modelo
      if (params['marca'] && !params['modelo']) {
        this.modelos.set(getModelsByBrand(fromSlug(params['marca'] || '')));
      } else {
        this.modelos.set([]);
      }

      // Actualizar meta tags cuando cambien los parámetros
      this.setMetaTags();
    });

    this.setAllCategories();
  }

  setAllCategories() {
    const allCategoriesSorted = [...(categorias?.grupos || [])].sort(
      (a: Category, b: Category) => a.name.localeCompare(b.name)
    );
    this.allCategories.set(allCategoriesSorted || []);
  }

  getCurrentQueryParams(): any {
    const params: any = {};
    if (this.marca()) params.marca = this.marca();
    if (this.modelo()) params.modelo = this.modelo();
    return params;
  }

  getVehicleText(): string {
    const parts: string[] = [];
    if (this.marca()) parts.push(smartTitle(this.marca()));
    if (this.modelo()) parts.push(smartTitle(this.modelo()));
    return parts.join(' ') || '';
  }

  private setMetaTags() {
    const vehiculo = this.getVehicleText();

    // Generar título dinámico basado en marca y modelo
    let pageTitle = `Repuestos para ${vehiculo || 'carro'} - Allas Repuestos`;
    this.h1Title.set(`Repuestos para ${vehiculo || 'carro'}`);

    let description = `Encontrá los repuestos para ${
      vehiculo || 'carro'
    } que necesitás en Allas Repuestos. Filtros, Frenos, Motor, Suspensión y más. ¡Calidad Garantizada y envío a todo Honduras!`;

    let keywords = `repuestos para ${vehiculo || 'carro'}, repuestos ${
      vehiculo || 'carro'
    }, ${vehiculo || 'carro'}, Honduras, Allas Repuestos, garantía, calidad`;

    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    // --- Título de la página ---
    this.title.setTitle(pageTitle);

    // --- Meta Descripción ---
    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    // --- Palabras Clave ---
    this.meta.updateTag({
      name: 'keywords',
      content: keywords,
    });

    // --- Open Graph Tags ---
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'Logo de Allas Repuestos',
    });

    // --- Twitter Tags ---
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    // --- Icono del sitio (favicon / logo principal) ---
    this.meta.updateTag({
      name: 'icon',
      content: imageUrl,
    });
  }

  getCategoryLink(slug: string): string[] {
    if (this.marca() && this.modelo()) {
      return ['/repuestos', slug, toSlug(this.marca()), toSlug(this.modelo())];
    }

    if (this.marca()) {
      return ['/repuestos', slug, toSlug(this.marca())];
    }

    return ['/repuestos', slug];
  }
}
